import { addDays, parseDate } from '@shared/time';
import { intlLocale, type Locale } from '@shared/i18n';

export interface CalendarCell {
  date: string; // YYYY-MM-DD
  day: number;
  inMonth: boolean;
  selectable: boolean;
  isToday: boolean;
}

export interface CalendarMonth {
  year: number;
  month: number; // 1-12
  label: string;
  weeks: CalendarCell[][];
}

/**
 * The weekday initials across the top of the calendar, in the language
 * the page is being read in. Generated from Intl rather than written
 * down, because a hard-coded 'Mon' is exactly the kind of string that
 * survives a translation pass unnoticed. Sunday-first, matching the grid
 * that buildMonth lays out.
 */
export function weekdayLabels(locale: Locale = 'en'): string[] {
  const format = new Intl.DateTimeFormat(intlLocale(locale), { timeZone: 'UTC', weekday: 'short' });
  // 2024-01-07 was a Sunday: seven consecutive days from it name the week.
  return Array.from({ length: 7 }, (_, i) => format.format(new Date(Date.UTC(2024, 0, 7 + i))));
}

function ymd(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** YYYY-MM of a date. */
export function monthOf(date: string): string {
  return date.slice(0, 7);
}

export function shiftMonth(yearMonth: string, delta: number): string {
  const [y, m] = yearMonth.split('-').map(Number);
  const t = new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1 + delta, 1));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * A 6-row grid for a month with days outside [minDate, maxDate] disabled.
 * Purely calendar arithmetic: the caller decides which zone "today" is in.
 */
export function buildMonth(yearMonth: string, today: string, minDate: string, maxDate: string, locale: Locale = 'en'): CalendarMonth {
  const [year, month] = yearMonth.split('-').map(Number) as [number, number];
  const first = new Date(Date.UTC(year, month - 1, 1));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const lead = first.getUTCDay();
  const start = addDays(ymd(year, month, 1), -lead);
  const weeks: CalendarCell[][] = [];
  let cursor = start;
  for (let w = 0; w < 6; w++) {
    const row: CalendarCell[] = [];
    for (let d = 0; d < 7; d++) {
      const p = parseDate(cursor)!;
      const inMonth = p.month === month;
      row.push({
        date: cursor,
        day: p.day,
        inMonth,
        selectable: inMonth && cursor >= minDate && cursor <= maxDate,
        isToday: cursor === today,
      });
      cursor = addDays(cursor, 1);
    }
    weeks.push(row);
    // Stop early once the month is done and the row ended in the next month.
    if (w >= 3 && parseDate(cursor)!.month !== month && (w + 1) * 7 >= lead + daysInMonth) break;
  }
  const label = new Intl.DateTimeFormat(intlLocale(locale), { timeZone: 'UTC', month: 'long', year: 'numeric' }).format(first);
  return { year, month, label, weeks };
}
